const ABSOLUTE_URL_RE = /^[a-z][a-z\d+.-]*:\/\//i;
const PROTOCOL_RELATIVE_URL_RE = /^\/\//;

type ParsedUrlParts = {
  leadingPart: string;
  finalSegment: string;
  search: string;
  hash: string;
  pathDepth: number;
  pathStartsWithSlash: boolean;
};

// Normalize different inputs into one shape so the shortening logic does not
// care whether it received `https://example.com/a/b`, `www.example.com/a/b`,
// or `/a/b`.
function createParsedUrlParts(
  leadingPart: string,
  path: string,
  search: string,
  hash: string,
): ParsedUrlParts {
  const pathSegments = path.split('/').filter(Boolean);

  return {
    leadingPart,
    finalSegment: pathSegments.at(-1) ?? '',
    search,
    hash,
    pathDepth: pathSegments.length,
    pathStartsWithSlash: path.startsWith('/'),
  };
}

function splitSuffix(value: string) {
  // Split `foo/bar?tab=1#details` into path, query, and hash before any slash
  // handling so that `?` and `#` are never mistaken for path content.
  const hashIndex = value.indexOf('#');
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex);
  const queryIndex = withoutHash.indexOf('?');

  if (queryIndex === -1) {
    return {
      base: withoutHash,
      search: '',
      hash,
    };
  }

  return {
    base: withoutHash.slice(0, queryIndex),
    search: withoutHash.slice(queryIndex),
    hash,
  };
}

function parseUrlParts(value: string): ParsedUrlParts | null {
  if (!value) {
    return null;
  }

  // Strings like `1http://example.com/...` look like absolute URLs but do not
  // have a valid scheme. Keep them unchanged instead of reinterpreting them as
  // a relative path with a collapsible middle.
  if (value.includes('://') && !ABSOLUTE_URL_RE.test(value)) {
    return null;
  }

  if (ABSOLUTE_URL_RE.test(value) || PROTOCOL_RELATIVE_URL_RE.test(value)) {
    try {
      // `new URL()` gives us reliable host/path parsing for full URLs.
      // Protocol-relative inputs like `//example.com/a/b` need a temporary
      // scheme to be parsed, but the returned `leadingPart` keeps the original
      // `//example.com` form.
      const parsedUrl = new URL(
        PROTOCOL_RELATIVE_URL_RE.test(value) ? `https:${value}` : value,
      );
      const leadingPart = PROTOCOL_RELATIVE_URL_RE.test(value)
        ? `//${parsedUrl.host}`
        : `${parsedUrl.protocol}//${parsedUrl.host}`;

      return createParsedUrlParts(
        leadingPart,
        parsedUrl.pathname,
        parsedUrl.search,
        parsedUrl.hash,
      );
    } catch {
      return null;
    }
  }

  // For inputs without a scheme, treat the first slash as the boundary between
  // the stable prefix and the collapsible path, for example:
  // `www.example.com/reports/2026/index.html` ->
  // leadingPart=`www.example.com`, path=`/reports/2026/index.html`
  const { base, search, hash } = splitSuffix(value);
  const firstSlashIndex = base.indexOf('/');
  const leadingPart =
    firstSlashIndex === -1 ? base : base.slice(0, firstSlashIndex);
  const path = firstSlashIndex === -1 ? '' : base.slice(firstSlashIndex);

  return createParsedUrlParts(leadingPart, path, search, hash);
}

function buildShortenedUrl(
  leadingPart: string,
  trailingPart: string,
  pathStartsWithSlash: boolean,
) {
  // Relative paths like `/foo/bar/baz` or `foo/bar/baz` do not have a host,
  // so the ellipsis form has to preserve whether the original path started
  // with a slash.
  if (!leadingPart) {
    return pathStartsWithSlash ? `/.../${trailingPart}` : `.../${trailingPart}`;
  }

  return `${leadingPart}/.../${trailingPart}`;
}

function buildShorteningCandidates(parsedUrl: ParsedUrlParts) {
  // Only shorten when there is an actual middle segment to remove.
  // `example.com/a` stays as-is, while `example.com/a/b` can become
  // `example.com/.../b`.
  if (parsedUrl.pathDepth < 2) {
    return [];
  }

  // Prefer to keep as much context as possible: first try `.../file?x=1#y`,
  // then `.../file#y`, then the bare `.../file` form.
  return [`${parsedUrl.search}${parsedUrl.hash}`, parsedUrl.hash, ''].map(
    (suffix) =>
      buildShortenedUrl(
        parsedUrl.leadingPart,
        `${parsedUrl.finalSegment}${suffix}`,
        parsedUrl.pathStartsWithSlash,
      ),
  );
}

function chooseShortestMeaningfulCandidate(
  originalUrl: string,
  maxLength: number,
  candidates: string[],
) {
  // Ignore candidates that are empty, duplicated, or not actually shorter than
  // the original. If nothing meaningful remains, returning the input is less
  // surprising than inventing a "shortened" URL that is not shorter.
  const meaningfulCandidates = Array.from(
    new Set(
      candidates.filter(
        (candidate) =>
          candidate.length > 0 && candidate.length < originalUrl.length,
      ),
    ),
  );

  if (meaningfulCandidates.length === 0) {
    return originalUrl;
  }

  // Return the first candidate that satisfies the requested limit. If none do,
  // still prefer the shortest meaningful form over a broken or misleading one.
  return (
    meaningfulCandidates.find((candidate) => candidate.length <= maxLength) ??
    meaningfulCandidates.reduce((shortestCandidate, candidate) =>
      candidate.length < shortestCandidate.length
        ? candidate
        : shortestCandidate,
    )
  );
}

export function getShortUrl(url: string, maxLength = 80) {
  if (url.length <= maxLength) {
    return url;
  }

  const parsedUrl = parseUrlParts(url);

  if (!parsedUrl) {
    return url;
  }

  return chooseShortestMeaningfulCandidate(
    url,
    maxLength,
    buildShorteningCandidates(parsedUrl),
  );
}
